import { Component, EventEmitter, inject, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core'
import { MatButton, MatIconButton } from '@angular/material/button'
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator'
import { MatTableDataSource, MatTableModule } from '@angular/material/table'
import { TranslateModule, TranslateService } from '@ngx-translate/core'
import { MatSortModule, Sort } from '@angular/material/sort'
import { MatIcon } from '@angular/material/icon'
import { MatTooltip } from '@angular/material/tooltip'
import { RouterLink } from '@angular/router'
import { DatePipe } from '@angular/common'
import { MatMenuModule } from '@angular/material/menu'
import { FormsModule } from '@angular/forms'
import { BritishDatePipe } from '../british-date.pipe'
import { DurationPipe } from '../duration.pipe'
import { ColumnType, PaginatedTableCellComponent } from './paginated-table-cell/paginated-table-cell.component'

export type PaginatedTableEvent = {
  page: number
  pageSize: number
  sortField: string | undefined
  sortDirection: 'asc' | 'desc'
}

export type ColumnDefinition<T> = {
  columnDef: string
  type: ColumnType
  header: string | ((element: T) => string)
  cell: (element: T) => any
  sortable: boolean
  sticky: boolean
  footerType?: ColumnType
  footer?: (data: T[]) => string | number | null
  highlight?: boolean
  downloadOnly?: boolean
}

export type AdminPaginatedTableAction<T> = { translationKey: string; icon: string; handle: (element: T) => void }

@Component({
  selector: 'app-paginated-table',
  standalone: true,
  imports: [
    MatTableModule,
    MatPaginatorModule,
    MatButton,
    TranslateModule,
    MatSortModule,
    MatIcon,
    MatIconButton,
    MatTooltip,
    RouterLink,
    FormsModule,
    MatMenuModule,
    PaginatedTableCellComponent,
  ],
  providers: [DatePipe, BritishDatePipe, DurationPipe],
  templateUrl: './paginated-table.component.html',
  styleUrl: './paginated-table.component.scss',
})
export class PaginatedTableComponent<T> implements OnInit, OnChanges {
  @Input({ required: true })
  data!: T[]

  @Input({ required: true })
  total!: number

  @Input({ required: true })
  columns!: ColumnDefinition<T>[]

  @Input()
  defaultPageSize: number = 100

  @Input()
  paginationEnabled = true

  @Input()
  actions: AdminPaginatedTableAction<T>[] = []

  @Input()
  generateEditLink: ((item: T) => (string | number)[]) | null = null

  @Input()
  displayFooterRow = false

  @Input({ required: true })
  timezone!: 'local' | 'british'

  @Input()
  csvDownloadEnabled = true

  @Input({ required: true })
  csvFilename!: string

  @Input()
  rowClass: ((element: T) => string | null) | null = null

  @Output()
  refetch = new EventEmitter<PaginatedTableEvent>()

  private translateService = inject(TranslateService)
  private datePipe = inject(DatePipe)
  private britishDatePipe = inject(BritishDatePipe)
  private durationPipe = inject(DurationPipe)

  pageIndex = 0
  pageSize!: number
  pageSizeOptions = [25, 100]
  sortField: string | null = null
  sortDirection: 'asc' | 'desc' = 'asc'

  dataSource = new MatTableDataSource<T>([])

  ngOnInit(): void {
    this.pageSize = this.defaultPageSize
    this.setupDataSource()
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data'] || changes['columns']) {
      this.setupDataSource()
    }
  }

  private setupDataSource(): void {
    if (this.data === null || this.data === undefined) {
      return
    }
    this.dataSource.data = this.data
  }

  getFooterValue(column: ColumnDefinition<T>): string | number | null {
    return column.footer ? column.footer(this.data) : null
  }

  get displayActionsColumn(): boolean {
    return this.actions.length > 0 || this.generateEditLink !== null
  }

  get displayedColumns(): string[] {
    const columns = this.columns.filter((column) => column.downloadOnly !== true).map((column) => column.columnDef)
    return this.displayActionsColumn ? [...columns, 'actions'] : columns
  }

  getRowClass(element: T): string | null {
    return this.rowClass !== null ? this.rowClass(element) : null
  }

  getHeader(column: ColumnDefinition<T>): string {
    if (typeof column.header === 'function') {
      return this.data.length ? column.header(this.data[0]) : column.columnDef
    }
    return column.header
  }

  emitRefetch() {
    this.refetch.emit({
      page: this.pageIndex,
      pageSize: this.pageSize,
      sortField: this.sortField ?? undefined,
      sortDirection: this.sortDirection,
    })
  }

  handlePageEvent(event: PageEvent) {
    this.pageSize = event.pageSize
    this.pageIndex = event.pageIndex
    this.emitRefetch()
  }

  handleSortEvent(e: Sort) {
    this.sortField = e.active
    this.sortDirection = e.direction === 'desc' ? 'desc' : 'asc'
    this.emitRefetch()
  }

  downloadCsv(): void {
    const headers = this.columns.map((column) => {
      const header = this.getHeader(column)
      return this.translateService.instant(header)
    })

    const rows = this.data.map((row) => {
      return this.columns.map((column) => {
        const cellData = column.cell(row)
        return this.formatCellValueForCsv(cellData, column.type)
      })
    })

    const csvContent = this.generateCsvContent(headers, rows)
    this.triggerDownload(csvContent, `${this.csvFilename}.csv`)
  }

  private formatCellValueForCsv(data: unknown, columnType: ColumnType): string {
    if (data === null || data === undefined) {
      return ''
    }

    const dateTransform = this.timezone === 'local' ? this.datePipe : this.britishDatePipe

    switch (columnType) {
      case 'BOOLEAN':
        return data ? 'Yes' : 'No'

      case 'DATE':
        return dateTransform.transform(data as Date) ?? ''

      case 'DURATION': {
        if (typeof data === 'object' && (data as { scheduled?: boolean }).scheduled !== undefined) {
          const value = data as { seconds: number; scheduled: boolean }
          const formatted = String(this.durationPipe.transform(value.seconds) ?? '')
          return value.scheduled ? `~${formatted} (scheduled)` : formatted
        }
        return String(this.durationPipe.transform(data as number) ?? '')
      }

      case 'CALL': {
        const call = data as { name?: string; id?: string }
        return call.name ?? ''
      }

      case 'USER': {
        const user = data as { email?: string; name?: string }
        return user.email ?? user.name ?? ''
      }

      case 'USER_AVATAR_LIST': {
        const users = data as Array<{ name?: string }>
        return users.map((user) => user.name ?? '').join(', ')
      }

      case 'ORGANIZATION': {
        const organization = data as { name?: string }
        return organization.name ?? ''
      }

      case 'CALL_STATUS':
      case 'COMPLETED_CALL_STATUS': {
        const status = data as { status?: string; processingStatus?: string; callType?: string }
        if (status.processingStatus !== null && status.processingStatus !== undefined) {
          return status.processingStatus
        }
        if (status.callType === 'VIDEO_UPLOAD' || status.callType === 'AUDIO_UPLOAD') {
          return 'COMPLETED'
        }
        return status.status ?? ''
      }

      case 'CALL_USERS': {
        const callUsers = data as Array<{ user?: { email?: string } }>
        return callUsers.map((callUser) => callUser.user?.email ?? '').join(', ')
      }

      case 'LIST': {
        const list = data as string[]
        return list.join(', ')
      }

      case 'JSON':
        return JSON.stringify(data)

      case 'MEETING_PROVIDER':
        return String(data)

      case 'NUMBER':
      case 'NUMBER_WITH_ONE_DECIMAL':
        return typeof data === 'number' ? String(data) : ''

      case 'UPLOADED_DOCUMENTS': {
        const documents = data as Array<{ resourceType?: string; fileName?: string }>
        return documents.map((document) => document.fileName ?? document.resourceType ?? '').join(', ')
      }

      case 'STARTS_IN': {
        if (data === null) {
          return ''
        }
        const targetDate = typeof data === 'string' ? new Date(data) : (data as Date)
        const now = new Date()
        const diffMs = targetDate.getTime() - now.getTime()
        if (diffMs <= 0) {
          return ''
        }
        const diffMinutes = Math.floor(diffMs / (1000 * 60))
        const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))
        if (diffDays > 0) {
          return `${diffDays}d`
        } else if (diffHours > 0) {
          const remainingMinutes = diffMinutes % 60
          if (remainingMinutes > 0) {
            return `${diffHours}h ${remainingMinutes}m`
          }
          return `${diffHours}h`
        } else {
          return `${diffMinutes}m`
        }
      }

      case 'TIME_RANGE': {
        if (data === null) {
          return ''
        }
        const range = data as { start: Date | string | null; end: Date | string | null }
        const startTime = range.start !== null ? new Date(range.start).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : ''
        const endTime = range.end !== null ? new Date(range.end).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : ''
        return `${startTime} - ${endTime}`
      }

      case 'TEXT':
      default:
        return String(data)
    }
  }

  private generateCsvContent(headers: string[], rows: string[][]): string {
    const escapeCsvValue = (value: string): string => {
      if (value.includes(',') || value.includes('"') || value.includes('\n')) {
        return `"${value.replace(/"/g, '""')}"`
      }
      return value
    }

    const headerLine = headers.map(escapeCsvValue).join(',')
    const dataLines = rows.map((row) => row.map(escapeCsvValue).join(','))

    return [headerLine, ...dataLines].join('\n')
  }

  private triggerDownload(content: string, filename: string): void {
    const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.setAttribute('href', url)
    link.setAttribute('download', filename)
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }
}
